import React, { useRef, useEffect, useState } from 'react';

const Btn = ({ onMouseDown, title, children, className = '' }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onMouseDown(); }}
    title={title}
    className={`min-w-[28px] h-7 px-1.5 flex items-center justify-center rounded text-sm font-medium text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition ${className}`}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-5 bg-gray-300 mx-0.5 flex-shrink-0" />;

function HtmlEditor({ value, onChange, editorKey }) {
  const editorRef = useRef(null);
  const [showSource, setShowSource] = useState(false);
  const [sourceVal, setSourceVal] = useState('');

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorKey]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    sync();
  };

  const sync = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const formatBlock = (tag) => exec('formatBlock', `<${tag}>`);

  const insertLink = () => {
    const sel = window.getSelection()?.toString();
    const url = window.prompt('Enter the URL:', 'https://');
    if (!url) return;
    if (sel) exec('createLink', url);
    else {
      const text = window.prompt('Link text:', 'Click here') || 'Click here';
      exec('insertHTML', `<a href="${url}" style="color:#059669;text-decoration:underline">${text}</a>`);
    }
  };

  const insertImage = () => {
    const url = window.prompt('Enter image URL:', 'https://');
    if (url) exec('insertHTML', `<img src="${url}" alt="" style="max-width:100%;border-radius:8px;margin:8px 0" />`);
  };

  const openSource = () => {
    setSourceVal(editorRef.current?.innerHTML || '');
    setShowSource(true);
  };

  const applySource = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = sourceVal;
      onChange(sourceVal);
    }
    setShowSource(false);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden shadow-sm bg-white">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-200 px-2 py-1 flex flex-wrap items-center gap-0.5">
        <Btn onMouseDown={() => exec('undo')} title="Undo (Ctrl+Z)">↩</Btn>
        <Btn onMouseDown={() => exec('redo')} title="Redo (Ctrl+Y)">↪</Btn>
        <Sep />

        <select
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { formatBlock(e.target.value); e.target.value = ''; }}
          defaultValue=""
          className="text-xs h-7 border border-gray-300 rounded px-1 bg-white text-gray-700 mr-1 cursor-pointer"
          title="Text style"
        >
          <option value="" disabled>Style</option>
          <option value="p">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
          <option value="blockquote">Quote</option>
          <option value="pre">Code</option>
        </select>
        <Sep />

        <Btn onMouseDown={() => exec('bold')} title="Bold (Ctrl+B)"><b>B</b></Btn>
        <Btn onMouseDown={() => exec('italic')} title="Italic (Ctrl+I)"><i>I</i></Btn>
        <Btn onMouseDown={() => exec('underline')} title="Underline (Ctrl+U)"><u>U</u></Btn>
        <Btn onMouseDown={() => exec('strikeThrough')} title="Strikethrough"><s>S</s></Btn>
        <Sep />

        <Btn onMouseDown={() => exec('justifyLeft')} title="Align Left">≡←</Btn>
        <Btn onMouseDown={() => exec('justifyCenter')} title="Center">≡</Btn>
        <Btn onMouseDown={() => exec('justifyRight')} title="Align Right">→≡</Btn>
        <Sep />

        <Btn onMouseDown={() => exec('insertUnorderedList')} title="Bullet List">• ≡</Btn>
        <Btn onMouseDown={() => exec('insertOrderedList')} title="Numbered List">1≡</Btn>
        <Btn onMouseDown={() => exec('outdent')} title="Decrease Indent">⇤</Btn>
        <Btn onMouseDown={() => exec('indent')} title="Increase Indent">⇥</Btn>
        <Sep />

        <Btn onMouseDown={insertLink} title="Insert Link">🔗</Btn>
        <Btn onMouseDown={insertImage} title="Insert Image">🖼</Btn>
        <Btn onMouseDown={() => exec('insertHorizontalRule')} title="Divider Line">─</Btn>
        <Btn onMouseDown={() => exec('removeFormat')} title="Clear Formatting">✕T</Btn>
        <Sep />

        <button
          type="button"
          onClick={showSource ? applySource : openSource}
          className="text-xs px-2 h-7 rounded border border-gray-300 text-gray-500 hover:bg-gray-100 transition ml-auto"
          title="Edit HTML Source"
        >
          {showSource ? 'Apply HTML' : '</> HTML'}
        </button>
      </div>

      {/* Source editor */}
      {showSource && (
        <textarea
          value={sourceVal}
          onChange={(e) => setSourceVal(e.target.value)}
          rows={12}
          className="w-full px-3 py-2 font-mono text-xs text-gray-900 bg-gray-50 focus:outline-none border-b border-gray-200"
          placeholder="Edit raw HTML here, then click 'Apply HTML'"
        />
      )}

      {/* WYSIWYG editor area */}
      {!showSource && (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={sync}
          className="min-h-[320px] max-h-[500px] overflow-y-auto p-4 focus:outline-none bg-white text-gray-900"
          style={{
            lineHeight: '1.8',
            fontSize: '15px',
          }}
        />
      )}

      <div className="bg-gray-50 border-t border-gray-200 px-3 py-1 text-[11px] text-gray-400">
        💡 Tip: Select text, then click toolbar buttons to format. Use <b>Style</b> dropdown for headings.
      </div>
    </div>
  );
}

export default HtmlEditor;
